import { addMonths, daysBetween, type IsoDate } from '../lib/dates.js'

/**
 * When the next supervision or appraisal falls due.
 *
 * This is the only implementation of that arithmetic. The database view
 * `review_cadence_resolved` deliberately stops at resolving the interval and
 * reporting the last meeting held: it does not compute a date. Two reasons.
 *
 * A stored or view-computed due date is a derived value that goes stale the
 * moment somebody edits the cadence, and 0016 already taught this project what
 * a stale date that looks authoritative costs. And a date computed in SQL from
 * current_date cannot be tested at its boundaries, whereas everything here
 * takes `today` as an argument.
 *
 * The front end imports this module too, so a manager and the nightly rules run
 * cannot disagree about whether a supervision is overdue.
 */

export type ReviewKind = 'supervision' | 'appraisal'

export interface Cadence {
  /** Months between supervisions. Zero means supervisions are not required. */
  supervisionIntervalMonths: number
  /** Months between appraisals. Zero means appraisals are not required. */
  appraisalIntervalMonths: number
  /** A tighter rhythm while still in probation. Null means use the ordinary one. */
  probationSupervisionIntervalMonths: number | null
  /** Days past the due date before 'due' becomes 'overdue'. */
  graceDays: number
}

export interface ReviewHistory {
  startDate: IsoDate
  probationEndDate: IsoDate | null
  lastSupervisionOn: IsoDate | null
  lastAppraisalOn: IsoDate | null
}

export type ReviewState = 'upcoming' | 'due_soon' | 'due' | 'overdue'

export interface ReviewDue {
  kind: ReviewKind
  intervalMonths: number
  /** The date the clock ran from. */
  anchor: IsoDate
  /** True when nobody has ever held one, so the start date is standing in. */
  neverHeld: boolean
  /** True when the shorter probation rhythm is the one in force. */
  onProbationRhythm: boolean
  dueOn: IsoDate
  daysUntilDue: number
  state: ReviewState
}

/**
 * How far ahead to start warning, derived from the cycle itself.
 *
 * A fixed number cannot serve both a monthly probation supervision and an
 * annual appraisal: 21 days would put a monthly cycle permanently in warning,
 * and 7 days would give a manager a week's notice of an appraisal. A sixth of
 * the cycle, bounded, behaves sensibly at both ends: 5 days for a monthly
 * rhythm, 15 for quarterly, 42 for annual.
 */
export function warnDaysFor(intervalMonths: number): number {
  const sixth = Math.round((intervalMonths * 30) / 6)
  return Math.min(42, Math.max(5, sixth))
}

/**
 * Which supervision interval applies. Probation is judged against `today`
 * rather than the anchor, because the question is what rhythm the person is on
 * now, not what they were on when they were last seen.
 */
export function supervisionIntervalFor(
  history: ReviewHistory,
  cadence: Cadence,
  today: IsoDate,
): { months: number; onProbationRhythm: boolean } {
  const probation = cadence.probationSupervisionIntervalMonths
  const stillInProbation =
    probation !== null &&
    history.probationEndDate !== null &&
    history.probationEndDate >= today
  return stillInProbation
    ? { months: probation, onProbationRhythm: true }
    : { months: cadence.supervisionIntervalMonths, onProbationRhythm: false }
}

/** Null when this kind of review is not required for this person. */
export function nextReviewDue(
  kind: ReviewKind,
  history: ReviewHistory,
  cadence: Cadence,
  today: IsoDate,
): ReviewDue | null {
  const resolved =
    kind === 'appraisal'
      ? { months: cadence.appraisalIntervalMonths, onProbationRhythm: false }
      : supervisionIntervalFor(history, cadence, today)

  if (resolved.months <= 0) return null

  const last = kind === 'appraisal' ? history.lastAppraisalOn : history.lastSupervisionOn
  const neverHeld = last === null
  const anchor = last ?? history.startDate
  const dueOn = addMonths(anchor, resolved.months)
  const daysUntilDue = daysBetween(today, dueOn)

  return {
    kind,
    intervalMonths: resolved.months,
    anchor,
    neverHeld,
    onProbationRhythm: resolved.onProbationRhythm,
    dueOn,
    daysUntilDue,
    state: stateFor(daysUntilDue, resolved.months, cadence.graceDays),
  }
}

export function stateFor(
  daysUntilDue: number,
  intervalMonths: number,
  graceDays: number,
): ReviewState {
  if (daysUntilDue < -graceDays) return 'overdue'
  if (daysUntilDue <= 0) return 'due'
  if (daysUntilDue <= warnDaysFor(intervalMonths)) return 'due_soon'
  return 'upcoming'
}

const KIND_LABEL: Record<ReviewKind, string> = {
  supervision: 'Supervision',
  appraisal: 'Appraisal',
}

export function describeReviewDue(due: ReviewDue, personName: string): string {
  const label = KIND_LABEL[due.kind]
  if (due.neverHeld) {
    return `${label} for ${personName} has never been held. The clock has been running from their start date of ${due.anchor}.`
  }
  return `${label} for ${personName} was last held on ${due.anchor}, and the cycle is every ${due.intervalMonths} month${due.intervalMonths === 1 ? '' : 's'}.`
}
